import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid, InputAdornment, LinearProgress, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router';

import { CREATE, DataAPIContext } from '../api';
import AccessDeniedView from '../components/AccessDeniedView';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import BlockRedirect from '../components/BlockRedirect';
import FormLicenseeContext from '../components/FormLicenseeContext';
import InfoTip from '../components/InfoTip';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import QuillEditor from '../components/QuillEditor';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import UploadDocumentButton from '../components/UploadDocumentButton';
import FormActionButtons from '../components/FormActionButtons';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';
import validURL from '../utils/validators/url';
import { FRIENDLY_NAME_HELP, FRIENDLY_NAME_MAX_LENGTH } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(8),
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    gridRoot: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    tabButton: {
        borderRadius: '30px',
        textTransform: 'capitalize',
        fontWeight: 'normal',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
}));

const validateInput = (item, isFranchise) => {
    const errors = {};
    let canSave = true;
    if (isFranchise && !item.tour_operator) {
        errors.tour_operator = { error: true, helperText: 'Licensee is required' };
        canSave = false;
    }
    if (!item.name || item.name.length <= 0) {
        if (item._changed && item._changed_name) {
            errors.name = { error: true, helperText: 'Name is required' };
        }
        canSave = false;
    }
    if (!item.item_type || item.item_type.length <= 0) {
        if (item._changed && item._changed_item_type) {
            errors.item_type = { error: true, helperText: 'Category is required' };
        }
        canSave = false;
    }
    return { canSave, errors };
};

const PreTripItemCreate = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'pre_trip_item';
    const history = useHistory();
    const classes = useStyles();
    const saveRef = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    // const [linkIsFile, setLinkIsFile] = React.useState(true);
    const [, setServerErrors] = React.useState([]);
    const { hasPerm, isFranchise, currentLicensee } = React.useContext(ContextStore);

    const { enqueueSnackbar } = useSnackbar();
    const [item, setItem] = React.useState({
        name: '',
        friendly_name: '',
        note: '',
        item_type: null,
        text: '',
        url: '',
        attachment: null,
        tour_operator: currentLicensee || null,
        _changed: false,
    });

    const [linkFileOrText, setLinkFileOrText] = React.useState(null);
    const onLicenseeChange = (id) => {
        setItem((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };
    const { canSave, errors } = validateInput(item, isFranchise);
    const [FileErrorState, setFileErrorState] = React.useState(false);
    const [LinkUrlErrorState, setLinkUrlErrorState] = React.useState(false);
    const [TextErrorState, setTextErrorState] = React.useState(false);
    let requiredUrl = linkFileOrText === 'link' && !item.url ? true : false;

    const handleChange = (ev) => {
        const fid = `_changed_${ev.target.name}`;
        setItem((prevState) => ({ ...prevState, [ev.target.name]: ev.target.value, _changed: true, [fid]: true }));
    };
    const handleCheckboxChange = (ev) => {
        setItem((prevState) => ({ ...prevState, [ev.target.name]: ev.target.checked, _changed: true }));
    };
    const onCategoryChange = (ev, nv) => {
        setItem((prevState) => ({ ...prevState, item_type: nv, _changed: true, _changed_item_type: true }));
    };
    const onAccessTagsChange = (ev, atags, reason) => {
        setItem((prevState) => ({ ...prevState, atag: atags, _changed: true }));
    };

    const onFileSelect = (file) => {
        setItem((prevState) => ({ ...prevState, attachment: file, upload: !!file, _changed: true }));
    };
    const handleSave = (ev) => {

        //Remove file/text/links based on radio selection
        if (linkFileOrText === 'link') {
            item.attachment = null;
            item.text = null;
        }
        if (linkFileOrText === 'text') {
            item.attachment = null;
            item.url = null;
        }
        if (linkFileOrText === 'file') {
            item.url = null;
            item.text = null;
        }

        if (!linkFileOrText || (!item.url && !item.text && !item.attachment)) {
            enqueueSnackbar('Please select the type of the trip item before saving', { variant: 'error' });
            return;
        }
        setSyncActive(true);
        disableMuiButton(saveRef.current);
        (async () => {
            try {
                const res = await dataAPI(CREATE, resource, { data: item, upload: item.upload });
                if (res.statusCode === 201) {
                    enqueueSnackbar('Trip item created', { variant: 'success' });
                    setItem({ ...item, _changed: false });
                    // Use a timeout to ensure the state has updated before redirecting
                    setTimeout(() => {
                        history.push(`/pretrip/items`);
                    }, 0);
                } else if (res.statusCode === 406) {
                    enqueueSnackbar('Trip item validation error: ' + res.data, { variant: 'warning' });
                    setServerErrors(res.data.errors);
                }
            } finally {
                setSyncActive(false);
                if (saveRef && saveRef.current) {
                    enableMuiButton(saveRef.current);
                }
            }
        })();
    };

    const handleLinkURLOnBlur = () => {
        if (validURL(item.url)) {
            setLinkUrlErrorState(false);
        } else {
            setLinkUrlErrorState(true);
        }
    };

    const handleChangeLinkFileText = (ev) => {
        setLinkFileOrText(null);

        if (ev.target.name === 'link') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }
        if (ev.target.name === 'text') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }
        if (ev.target.name === 'file') {
            if (ev.target.checked) {
                setLinkFileOrText(ev.target.name);
            }
        }
        props.setDetailsDirty && props.setDetailsDirty(true);
    };

    React.useEffect(() => {
        setFileErrorState(false);
        setTextErrorState(false);
        if (linkFileOrText === 'file') {
            if (!item.attachment) {
                setFileErrorState(true);
            }
        }

        if (linkFileOrText === 'text') {
            if (!item.text || item.text === '') {
                setTextErrorState(true);
            }
        }
    }, [item, linkFileOrText]);

    if (!hasPerm('pre_trip_item', 'write')) {
        return <AccessDeniedView />;
    }
    return (
        <div className={classes.root}>
            <PageMeta title="Create New Trip Item" />
            <BlockRedirect shouldBlock={item._changed} message="Are you sure you want to leave without saving changes?" />
            <FormLicenseeContext.Provider value={item.tour_operator}>
            <div className={classes.gridRoot}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <LinearProgress color="primary" style={{ visibility: syncActive ? 'visible' : 'hidden' }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Create New Trip Item</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <LicenseeFormField value={item.tour_operator} onChange={onLicenseeChange} error={errors.tour_operator?.error} helperText={errors.tour_operator?.helperText} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField helperText={`${64 - item.name.length} characters left`} {...errors.name} required variant="filled" size="small" fullWidth name="name" label="Name" value={item.name} onChange={handleChange} inputProps={{ maxLength: 64 }} />
                    </Grid>
                    <Grid item xs={12}>
                        {/* CP-6187: what guests see in the app; the backend stores a blank as NULL. */}
                        <TextField
                            helperText={`${FRIENDLY_NAME_MAX_LENGTH - item.friendly_name.length} characters left`}
                            variant="filled"
                            size="small"
                            fullWidth
                            name="friendly_name"
                            label="Friendly Name (optional)"
                            value={item.friendly_name}
                            onChange={handleChange}
                            inputProps={{ maxLength: FRIENDLY_NAME_MAX_LENGTH }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <InfoTip placement="top-end" title={FRIENDLY_NAME_HELP} sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField variant="filled" size="small" fullWidth name="note" label="Note" value={item.note || ''} onChange={handleChange} />
                    </Grid>
                    <Grid item xs={12}>
                        <RemoteAutocomplete required resource="pre_trip_item_type" urlSuffix={null} label="Choose Category" variant="filled" size="small" onChange={onCategoryChange} value={item.item_type} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography style={{ fontWeight: 'bold' }}>Display When Tapped</Typography>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox name="link" color="primary" onChange={handleChangeLinkFileText} checked={linkFileOrText === 'link'} />} label="Link" />
                            {linkFileOrText === 'link' && (
                                <>
                                    <div style={{ display: 'flex', marginTop: '1rem' }}>
                                        <TextField
                                            {...errors.url}
                                            variant="filled"
                                            size="small"
                                            fullWidth
                                            name="url"
                                            label="Link"
                                            placeholder="http://example.com"
                                            value={item.url || ''}
                                            onChange={handleChange}
                                            onBlur={handleLinkURLOnBlur}
                                            error={LinkUrlErrorState}
                                            helperText={LinkUrlErrorState && 'Please enter a valid URL.'}
                                        />
                                    </div>
                                    <FormGroup>
                                        <FormControlLabel control={<Checkbox name="deep_link" color="primary" onChange={handleCheckboxChange} checked={item.deep_link} />} label="Deep Link" />
                                        <FormHelperText>Open in Safari vs. In-App Browser</FormHelperText>
                                    </FormGroup>
                                </>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox name="file" color="primary" onChange={handleChangeLinkFileText} checked={linkFileOrText === 'file'} />} label="File" />
                            {linkFileOrText === 'file' && (
                                <Box pt={1} pb={1}>
                                    <UploadDocumentButton file={item.attachment} onSelect={onFileSelect} size="small" />
                                </Box>
                            )}
                        </FormGroup>
                        {item.item_type?.rendering_type !== 2 && (
                            <FormGroup>
                                <FormControlLabel control={<Checkbox name="text" color="primary" onChange={handleChangeLinkFileText} checked={linkFileOrText === 'text'} />} label="Text" />
                                {linkFileOrText === 'text' && item.item_type?.name !== 'List without checkboxes' && 
                                    <QuillEditor {...errors.text} name="text" label="Text" value={item.text || ''} onChange={(content) => handleChange({target: {name: 'text', value: content}})} />
                                }
                            </FormGroup>
                        )}

                        {/* <FormControl style={{ width: '100%', marginTop: '1rem' }}>
                        <RadioGroup
                            aria-labelledby="display-when-tapped"
                            name="radio-buttons-group"
                            value={linkFileOrText}
                            onChange={(e) => handleChangeRadio(e)}
                        >
                            <FormControlLabel value="link" control={<Radio color="primary" />} label="Link" />
                            {linkFileOrText === "link" &&
                                <Button color="primary" variant="outlined" size="small" onClick={toggleLinkIsFile}>
                                    <InsertLinkIcon className={classes.icon} /> Add Link
                                </Button>
                            }

                            <FormControlLabel value="file" control={<Radio color="primary" />} label="File" />


                        </RadioGroup>
                    </FormControl> */}
                    </Grid>
                    <Grid item xs={12}>
                        <AccessTagsDropdown value={item.atag} onChange={onAccessTagsChange} />
                    </Grid>
                </Grid>
            </div>
            <Box p={1} className={classes.buttonPanel}>
                <FormActionButtons
                    submitRef={saveRef}
                    onSubmit={handleSave}
                    submitDisabled={!canSave || LinkUrlErrorState || FileErrorState || TextErrorState || requiredUrl}
                    onCancel={() => history.push('/pretrip/items')}
                />
            </Box>
            </FormLicenseeContext.Provider>
        </div>
    );
};

export default PreTripItemCreate;
